import { useCallback, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";

import LoadingSvg2 from "./LoadingSvg2";
import OpenEnrollmentDao from "../dao/OpenEnrollmentDao";
import SchoolCard from "./SchoolCard";
import T from "./translations/Translations";

import { GlobalContext } from "./contextProvider/ContextProvider";

/**
 * @name ExpandInfo
 * @description ExpandInfo component
 * @param {func} setExpand
 * @param {{}} student
 * @returns {JSX.Element} ExpandInfo component
 */
const ExpandInfo = ({ setExpand, student }) => {
    const { dispatch, lang, state } = useContext(GlobalContext);
    const { studentAppObjects, token } = state || {};

    const [loading, setLoading] = useState(true);
    const [schoolChoices, setSchoolChoices] = useState([]);

    const studentKey = student?.key;
    const existing = studentAppObjects?.[studentKey]?.schoolChoices || null;

    const { schoolChoiceDetailDtos } = student;

    /**
     * @name dispatchAppObject
     * @description Dispatch the studentAppObjects to the context
     * @param {[]} arr
     * @callback
     */
    const dispatchAppObject = useCallback(
        (arr) => {
            dispatch({
                type: "StudentAppObjects",
                studentAppObjects: {
                    ...studentAppObjects,
                    [studentKey]: {
                        ...studentAppObjects?.[studentKey],
                        schoolChoices: arr
                    }
                }
            });
        },
        [dispatch, studentAppObjects, studentKey]
    );

    /**
     * Load a students school choices from context if they exist
     */
    useEffect(() => {
        if (existing) {
            setSchoolChoices(existing);
            setLoading(false);
        }
    }, [existing]);

    /**
     * Do not show 'More Info' button if there are no school choices
     * Fetch participating schools and their details
     */
    useEffect(() => {
        if (existing) {
            return;
        }
        if (schoolChoiceDetailDtos?.length < 1 || !token) {
            return;
        }

        /**
         * @name fetchParticipatingSchools
         * @description Get a list of participating schools for the student
         * @async
         */
        const fetchParticipatingSchools = async () => {
            try {
                const schoolPromises = schoolChoiceDetailDtos.map(async (schoolChoice) => {
                    const { participatingSchoolId } = schoolChoice;
                    const options = {
                        action: "participatingSchoolRead",
                        key: participatingSchoolId,
                        params: {
                            fetchLocationData: true
                        },
                        token
                    };
                    try {
                        const response = await OpenEnrollmentDao(options);
                        if (response && response.data) {
                            const { payload } = response.data;
                            payload.status = schoolChoice.status;
                            payload.kinderChoice = schoolChoice.kinderChoice;
                            payload.kinderOffer = schoolChoice.kinderOffer;
                            payload.languageChoice = schoolChoice.languageChoice;
                            payload.languageOffer = schoolChoice.languageOffer;

                            return payload;
                        }
                    } catch (error) {
                        console.error(`Unable to retrieve details for student ${student.key}:\n`, error);
                    }
                });
                // Resolve all promises and filter out null results
                const participatingSchools = (await Promise.all(schoolPromises)).filter(Boolean);
                dispatchAppObject(participatingSchools);
                // setSchoolChoices(participatingSchools);
            } catch (error) {
                console.error("Unable to retrieve Open Enrollment details:\n", error);
            }
        };

        fetchParticipatingSchools();
    }, [dispatchAppObject, existing, lang, schoolChoiceDetailDtos, student, token]);

    return (
        <div className="fade-style">
            <div style={{ margin: "15px 0 -25px 0" }}>
                <i>
                    <b>
                        {/* School Choices */}
                        {T({ key: "OEApp_SchoolChoices" })}
                    </b>
                </i>
            </div>
            <br />
            <div className="more-info-schools-container">
                {schoolChoices?.length > 0 &&
                    schoolChoices.map((school, index) => {
                        const uniqueKey = `school-${school.key}-${index}`;
                        return <SchoolCard key={uniqueKey} school={school} />;
                    })}
                {loading && <LoadingSvg2 />}
            </div>
            <div className="more-info-button-container">
                <button className="main-info-cancel" onClick={() => setExpand(false)} type="button">
                    {T({ key: "Global_Close" })}
                </button>
            </div>
        </div>
    );
};

ExpandInfo.propTypes = {
    setExpand: PropTypes.func.isRequired,
    student: PropTypes.oneOfType([PropTypes.object]).isRequired
};

export default ExpandInfo;
